/**
 * VerifyEmailPage — landing page for the link in the verification email.
 *
 * Reads ?token=... from the URL, calls /api/auth/verify-email/{token},
 * then redirects to Settings → Profile (or Login if user isn't signed in).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { verifyEmail } from '../services/api';

export default function VerifyEmailPage() {
  const [state, setState] = useState('verifying'); // verifying | ok | already | error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      setState('error');
      setMessage('No verification token in the URL.');
      return;
    }
    verifyEmail(token)
      .then(d => {
        if (d?.alreadyVerified) { setState('already'); setMessage('Your email was already verified.'); }
        else if (d?.verified) { setState('ok'); setMessage('Your email is now verified.'); }
        else { setState('error'); setMessage('Verification failed.'); }
      })
      .catch(err => {
        setState('error');
        setMessage(err.message || 'Verification failed.');
      });
  }, []);

  // Auto-redirect on success after 3 seconds
  useEffect(() => {
    if (state === 'ok' || state === 'already') {
      const t = setTimeout(() => {
        navigate('/settings?section=profile', { replace: true });
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [state, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-dark-900">
      <div className="max-w-md w-full bg-dark-800 rounded-3xl border border-dark-700/50 p-8 text-center">
        {state === 'verifying' && (
          <>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-violet-500/15 flex items-center justify-center mb-4">
              <Loader2 className="w-7 h-7 text-violet-400 animate-spin" />
            </div>
            <h1 className="text-xl font-bold text-white">Verifying your email…</h1>
            <p className="text-sm text-gray-400 mt-2">This will just take a moment.</p>
          </>
        )}
        {(state === 'ok' || state === 'already') && (
          <>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/15 flex items-center justify-center mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-400" />
            </div>
            <h1 className="text-xl font-bold text-white">Email verified</h1>
            <p className="text-sm text-gray-400 mt-2">{message}</p>
            <p className="text-xs text-gray-500 mt-4">Redirecting you to Settings…</p>
            <Link to="/settings?section=profile"
              className="inline-block mt-3 px-4 py-2 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold">
              Go to Settings →
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-red-500/15 flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7 text-red-400" />
            </div>
            <h1 className="text-xl font-bold text-white">Verification failed</h1>
            <p className="text-sm text-gray-400 mt-2">{message}</p>
            <p className="text-xs text-gray-500 mt-4">
              The link may have expired or been used already. Open Settings → Profile and click "Send verification" to get a fresh link.
            </p>
            <Link to="/settings?section=profile"
              className="inline-block mt-3 px-4 py-2 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold">
              Open Settings
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
